import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'
import {
  ContentAnalysisErrorResponse,
  ContentAnalysisPayload,
  ContentAnalysisSingleResponse,
  ContentAnalysisValidationErrorResponse,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'

interface UpdateContentAnalysisArgs {
  id: string
  payload: ContentAnalysisPayload
}

interface UseUpdateContentAnalysisMutationParams {
  onError?: () => void
  onSuccess?: (data: ContentAnalysisSingleResponse) => void
}

type ApiErrorResponse =
  | ContentAnalysisErrorResponse
  | ContentAnalysisValidationErrorResponse

const getErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
  const data = error.response?.data
  if (!data) return en.CONTENT_ANALYSIS_UPDATE_ERROR

  if (
    'errors' in data &&
    Array.isArray(data.errors) &&
    data.errors.length > 0
  ) {
    return data.errors.join(', ')
  }

  if (data.error) {
    return data.error
  }

  return en.CONTENT_ANALYSIS_UPDATE_ERROR
}

export const useUpdateContentAnalysisMutation = ({
  onError,
  onSuccess,
}: UseUpdateContentAnalysisMutationParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: UpdateContentAnalysisArgs) =>
      ContentAnalysisApiService.update(id, payload),
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(getErrorMessage(error))
      onError?.()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONTENT_ANALYSIS_LIST],
      })
      toast.success(en.CONTENT_ANALYSIS_UPDATE_SUCCESS)
      onSuccess?.(data)
    },
  })
}
